<template>
    <Head title="Service Categories" />

    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <!-- Header -->
                <div class="mb-6">
                    <div class="flex items-center justify-between">
                        <div class="flex items-center gap-3">
                            <div>
                                <h1 class="text-2xl font-semibold text-gray-900 dark:text-white">Service Categories</h1>
                                <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">Manage maintenance service categories</p>
                            </div>
                            <InfoButton title="Service Categories Management" variant="default">
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                    Service Categories organize maintenance services into logical groups like Electrical, Plumbing,
                                    HVAC, or Mechanical. These categories help organize the service catalog, enable filtering in work
                                    orders, and support reporting by maintenance type. Categories are used when creating services and
                                    can be linked to SLA policies for automated response time management.
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                    • <strong>Add Category:</strong> Click "Add Category" to create a new service category with name
                                      and description<br>
                                    • <strong>Search:</strong> Filter categories by name using the search box<br>
                                    • <strong>Status Filter:</strong> View all categories, only active ones, or only inactive ones<br>
                                    • <strong>Services Column:</strong> Shows how many services are assigned to each category<br>
                                    • <strong>Work Orders Column:</strong> Shows how many work orders use services from this category<br>
                                    • <strong>Edit:</strong> Modify category name or description<br>
                                    • <strong>Activate/Deactivate:</strong> Toggle category status - inactive categories hide their
                                      services from work order creation<br>
                                    • <strong>Delete:</strong> Only available if the category has no associated work orders
                                </p>
                                <p>
                                    <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                    • <strong>Start simple</strong> - Create broad categories first (Electrical, Plumbing, HVAC),
                                      then add specific services within them<br>
                                    • <strong>Consistent naming</strong> - Use clear, standard industry terms for easy recognition<br>
                                    • <strong>Deactivate not delete</strong> - Inactive categories preserve history while hiding
                                      services from new work orders<br>
                                    • <strong>Plan for SLAs</strong> - Categories can be linked to SLA policies, so organize by
                                      response time requirements<br>
                                    • <strong>Service count</strong> shows adoption - categories with many services indicate commonly
                                      used maintenance types
                                </p>
                            </InfoButton>
                        </div>
                        <Link
                            :href="route('settings.maintenance.service-categories.create')"
                            class="inline-flex items-center px-4 py-2 bg-blue-600 dark:bg-blue-500 border border-transparent rounded-md font-semibold text-xs text-white uppercase tracking-widest hover:bg-blue-700 dark:hover:bg-blue-600 focus:bg-blue-700 active:bg-blue-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition ease-in-out duration-150"
                        >
                            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                            </svg>
                            Add Category
                        </Link>
                    </div>
                </div>

                <!-- Filters -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow mb-6 p-6 border border-gray-200 dark:border-gray-700">
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <!-- Search -->
                        <div>
                            <label for="search" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Search</label>
                            <input
                                id="search"
                                v-model="filters.search"
                                type="text"
                                placeholder="Search by name..."
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @input="debouncedSearch"
                            />
                        </div>

                        <!-- Active Filter -->
                        <div>
                            <label for="active" class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Status</label>
                            <select
                                id="active"
                                v-model="filters.active"
                                class="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                @change="search"
                            >
                                <option value="">All</option>
                                <option value="1">Active</option>
                                <option value="0">Inactive</option>
                            </select>
                        </div>

                        <!-- Reset Button -->
                        <div class="flex items-end">
                            <button
                                @click="resetFilters"
                                class="w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 dark:focus:ring-gray-400"
                            >
                                Reset Filters
                            </button>
                        </div>
                    </div>
                </div>

                <!-- Categories Table -->
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden border border-gray-200 dark:border-gray-700">
                    <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                        <thead class="bg-gray-50 dark:bg-gray-700">
                            <tr>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Category
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Services
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Work Orders
                                </th>
                                <th scope="col" class="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" class="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody class="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            <tr v-for="category in categories.data" :key="category.id" class="hover:bg-gray-50 dark:hover:bg-gray-700">
                                <td class="px-6 py-4">
                                    <div class="flex items-center">
                                        <div class="ml-0">
                                            <div class="text-sm font-medium text-gray-900 dark:text-white">
                                                {{ category.name }}
                                            </div>
                                            <div v-if="category.description" class="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                                                {{ category.description }}
                                            </div>
                                        </div>
                                    </div>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ category.services_count }} services
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {{ category.work_orders_count }} orders
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap">
                                    <span
                                        :class="[
                                            'px-2 inline-flex text-xs leading-5 font-semibold rounded-full',
                                            category.is_active
                                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                                                : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200'
                                        ]"
                                    >
                                        {{ category.is_active ? 'Active' : 'Inactive' }}
                                    </span>
                                </td>
                                <td class="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    <div class="flex items-center justify-end space-x-2">
                                        <Link
                                            :href="route('settings.maintenance.service-categories.edit', category.id)"
                                            class="text-blue-600 dark:text-blue-400 hover:text-blue-900 dark:hover:text-blue-300"
                                        >
                                            Edit
                                        </Link>
                                        <button
                                            @click="toggleActive(category)"
                                            class="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200"
                                        >
                                            {{ category.is_active ? 'Deactivate' : 'Activate' }}
                                        </button>
                                        <button
                                            v-if="category.work_orders_count === 0"
                                            @click="deleteCategory(category)"
                                            class="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                                        >
                                            Delete
                                        </button>
                                    </div>
                                </td>
                            </tr>
                            <tr v-if="categories.data.length === 0">
                                <td colspan="5" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
                                    No service categories found
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <!-- Pagination -->
                    <div v-if="categories.data.length > 0" class="bg-white dark:bg-gray-800 px-4 py-3 border-t border-gray-200 dark:border-gray-700 sm:px-6">
                        <Pagination :links="categories.links" />
                </div>
            </div>
    </div>
</template>

<script setup>
import { ref, watch } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import { debounce } from 'lodash';
import Pagination from '@/Components/Pagination.vue';
import { useToast } from '@/Composables/useToast';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    categories: Object,
    filters: Object,
});

const toast = useToast();

const filters = ref({
    search: props.filters.search || '',
    active: props.filters.active || '',
});

const search = () => {
    router.get(route('settings.maintenance.service-categories.index'), filters.value, {
        preserveState: true,
        preserveScroll: true,
    });
};

const debouncedSearch = debounce(search, 300);

const resetFilters = () => {
    filters.value = {
        search: '',
        active: '',
    };
    search();
};

const toggleActive = (category) => {
    router.post(
        route('settings.maintenance.service-categories.toggleActive', category.id),
        {},
        {
            preserveScroll: true,
            onSuccess: () => {
                toast.success(`Category ${category.is_active ? 'deactivated' : 'activated'} successfully`);
            },
            onError: () => {
                toast.error('Failed to update category status');
            },
        }
    );
};

const deleteCategory = (category) => {
    if (!confirm(`Are you sure you want to delete "${category.name}"? This action cannot be undone.`)) {
        return;
    }

    router.delete(route('settings.maintenance.service-categories.destroy', category.id), {
        preserveScroll: true,
        onSuccess: () => {
            toast.success('Category deleted successfully');
        },
        onError: (errors) => {
            toast.error(errors.error || 'Failed to delete category');
        },
    });
};
</script>
